import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Plus, Search, Contact, CheckCircle2, XCircle, FileText } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { BottomSheet, Empty, DeleteButton, fmtData, BulkCheck, BulkBar, ConfirmAction, BadgeFoto, SelettoreStagione } from "@/components/Shared";
import { useRefreshOnPull } from "@/components/PullToRefresh";
import AnimatoForm from "@/components/AnimatoForm";
import RiepilogoIscrizione from "@/components/RiepilogoIscrizione";

export default function Animati() {
  const [animati, setAnimati] = useState([]);
  const [search, setSearch] = useState("");
  const [sel, setSel] = useState(null);
  // Dichiarazione dei genitori: caricata su richiesta, non insieme all'elenco.
  // Contiene i consensi e va tracciata come consultazione, quindi si scarica
  // solo quando qualcuno la apre davvero.
  const [riepilogo, setRiepilogo] = useState(null);

  useEffect(() => {
    if (!riepilogo?.id || riepilogo.dati) return;
    let vivo = true;
    api.get(`/animati/${riepilogo.id}/riepilogo`)
      .then((r) => vivo && setRiepilogo((x) => (x?.id === riepilogo.id ? { ...x, dati: r.data } : x)))
      .catch(() => vivo && toast.error("Dichiarazione non disponibile"));
    return () => { vivo = false; };
  }, [riepilogo?.id, riepilogo?.dati]);
  const [nuovoOpen, setNuovoOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [bulkMode, setBulkMode] = useState(false);
  const [selected, setSelected] = useState([]);

  // Stagione osservata. Vuota = quella corrente, che è ciò che vede chiunque:
  // il selettore compare solo all'Amministratore (vedi SelettoreStagione).
  const [stagione, setStagione] = useState("");
  const load = useCallback(
    () => api.get(`/animati${stagione ? `?stagione=${encodeURIComponent(stagione)}` : ""}`)
      .then((r) => setAnimati(r.data)),
    [stagione]);
  useEffect(() => { load(); }, [load]);
  useRefreshOnPull(load);

  const filtered = animati.filter((a) =>
    `${a.nome} ${a.cognome} ${a.codice_fiscale}`.toLowerCase().includes(search.toLowerCase())
  );

  const crea = async (payload) => {
    setSaving(true);
    try { await api.post("/animati", payload); toast.success("Animato iscritto!"); setNuovoOpen(false); load(); }
    catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  const modifica = async (payload) => {
    setSaving(true);
    try { await api.put(`/animati/${sel.id}`, payload); toast.success("Anagrafica aggiornata"); setSel(null); load(); }
    catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  const elimina = async () => {
    try { await api.delete(`/animati/${sel.id}`); toast.success("Animato eliminato"); setSel(null); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  const toggleSel = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const selectAll = () => setSelected(selected.length === filtered.length ? [] : filtered.map((a) => a.id));
  const exitBulk = () => { setBulkMode(false); setSelected([]); };

  const bulkDelete = async () => {
    try {
      const { data } = await api.post("/animati/bulk/delete", { ids: selected });
      toast.success(`${data.deleted} animati eliminati`);
      exitBulk(); load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const bulkQuota = async (paid) => {
    try {
      const { data } = await api.post("/animati/bulk/quota", { ids: selected, quota_pagata: paid });
      toast.success(`Quota aggiornata su ${data.updated} animati`);
      exitBulk(); load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div data-testid="animati-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-extrabold text-slate-900">Animati</h1>
          <div className="mt-2">
            <SelettoreStagione valore={stagione} onChange={setStagione} testId="animati-stagione" />
          </div>
          <p className="text-sm text-slate-500 font-semibold">{animati.length} iscritti</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {animati.length > 0 && (
            <button className={bulkMode ? "btn-secondary" : "btn-ghost"} onClick={() => bulkMode ? exitBulk() : setBulkMode(true)} data-testid="animati-bulk-toggle">
              {bulkMode ? "Annulla selezione" : "Seleziona"}
            </button>
          )}
          <button className="btn-primary" onClick={() => setNuovoOpen(true)} data-testid="nuovo-animato-btn">
            <Plus className="h-4 w-4" /> Nuovo Animato
          </button>
        </div>
      </div>

      {bulkMode && (
        <BulkBar count={selected.length} total={filtered.length} onSelectAll={selectAll} onClear={exitBulk} testId="animati-bulk-bar"
          actions={
            <>
              <ConfirmAction onConfirm={() => bulkQuota(true)} title={`Segnare quota come PAGATA per ${selected.length} Animati?`} testId="animati-bulk-quota-paid-confirm">
                <button type="button" disabled={selected.length === 0} className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold px-3 py-2 disabled:opacity-40 inline-flex items-center gap-1.5" data-testid="animati-bulk-quota-paid-btn">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Quota pagata
                </button>
              </ConfirmAction>
              <ConfirmAction onConfirm={() => bulkQuota(false)} title={`Segnare quota come NON PAGATA per ${selected.length} Animati?`} testId="animati-bulk-quota-unpaid-confirm">
                <button type="button" disabled={selected.length === 0} className="rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold px-3 py-2 disabled:opacity-40 inline-flex items-center gap-1.5" data-testid="animati-bulk-quota-unpaid-btn">
                  <XCircle className="h-3.5 w-3.5" /> Non pagata
                </button>
              </ConfirmAction>
              <ConfirmAction onConfirm={bulkDelete} title={`Eliminare ${selected.length} Animati?`} description="Verranno rimossi anche dalle squadre in cui erano assegnati." testId="animati-bulk-delete-confirm">
                <button type="button" disabled={selected.length === 0} className="btn-danger disabled:opacity-40 !py-2" data-testid="animati-bulk-delete-btn">
                  Elimina {selected.length > 0 && `(${selected.length})`}
                </button>
              </ConfirmAction>
            </>
          } />
      )}

      <div className="relative mb-5">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <input type="search" className="input-app !pl-11" placeholder="Cerca per nome o codice fiscale..." value={search} onKeyDown={(e) => { if (e.key === "Enter") e.preventDefault(); }} onChange={(e) => setSearch(e.target.value)} data-testid="animati-search-input" />
      </div>

      {filtered.length === 0 ? (
        <Empty icon={Contact} text="Nessun Animato trovato" sub="Iscrivi il primo Animato dal pulsante in alto" />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map((a, i) => {
            const isSel = selected.includes(a.id);
            return (
              <motion.div
                key={a.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(i * 0.03, 0.4) }}
                onClick={() => bulkMode ? toggleSel(a.id) : setSel(a)}
                className={`card-app p-4 text-left cursor-pointer hover:-translate-y-1 hover:shadow-lg transition-all ${bulkMode && isSel ? "ring-2 ring-amber-400" : ""}`}
                data-testid={`animato-card-${a.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  {bulkMode && <BulkCheck checked={isSel} onClick={() => toggleSel(a.id)} testId={`animato-check-${a.id}`} />}
                  <div className="min-w-0 flex-1">
                    <p className="font-display font-extrabold text-slate-900 truncate">{a.nome} {a.cognome}</p>
                    <p className="text-xs text-slate-400 font-semibold">{a.squadra_nome || "Senza squadra"} · Taglia {a.taglia_maglia}</p>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {a.quota_pagata
                    ? <span className="badge-pos">Quota pagata{a.quota_pagata_data ? ` · ${fmtData(a.quota_pagata_data)}` : ""}</span>
                    : <span className="badge-neg">Quota non pagata</span>}
                  <BadgeFoto animato={a} />
                  {a.note_da_revisionare && <span className="badge-warn">Note da riclassificare</span>}
                  {a.contatti_da_normalizzare && <span className="badge-warn">Contatti da completare</span>}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      <BottomSheet open={nuovoOpen} onOpenChange={setNuovoOpen} title="Nuovo Animato" testId="nuovo-animato-sheet">
        <AnimatoForm onSubmit={crea} saving={saving} submitLabel="Iscrivi" />
      </BottomSheet>

      <BottomSheet open={!!sel} onOpenChange={(v) => !v && setSel(null)} title={sel ? `${sel.nome} ${sel.cognome}` : ""} description={sel?.squadra_nome ? `Squadra: ${sel.squadra_nome}` : "Non ancora assegnato a una squadra"} testId="animato-detail-sheet">
        {sel && (
          <>
            {/* Copia di quanto dichiarato dai genitori. Non è un file salvato:
                viene rigenerato dai dati archiviati, così quando l'anagrafica
                viene cancellata sparisce con essa invece di restare in giro. */}
            <div className="mb-4 flex flex-wrap gap-2">
              <button type="button" className="btn-ghost !py-2 text-xs"
                onClick={() => setRiepilogo(riepilogo?.id === sel.id ? null : { id: sel.id, caricamento: true })}
                data-testid="animato-riepilogo-btn">
                <FileText className="h-3.5 w-3.5" />
                {riepilogo?.id === sel.id ? "Nascondi dichiarazione" : "Vedi dichiarazione dei genitori"}
              </button>
            </div>
            {riepilogo?.id === sel.id && riepilogo.dati && (
              <div className="mb-4 border border-slate-200 rounded-3xl overflow-hidden">
                <RiepilogoIscrizione dati={riepilogo.dati} />
              </div>
            )}

            <AnimatoForm initial={sel} onSubmit={modifica} saving={saving} submitLabel="Salva modifiche" />
            <div className="mt-4 flex justify-end">
              <DeleteButton onConfirm={elimina} entity={`${sel.nome} ${sel.cognome}`} testId="animato-delete-btn" />
            </div>
          </>
        )}
      </BottomSheet>
    </div>
  );
}
